import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Ban, ClipboardCopy, MailCheck, Plus, Save, TicketCheck, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";

import { ErrorState, LoadingState, MutationMessage, PageHeader, Section, StatusBadge } from "../account/components";
import { RegistrationAdministration, RegistrationPolicy, api, formatDate } from "../api";

type PolicyForm = {
  registrationEnabled: boolean;
  invitationRequired: boolean;
  emailVerificationRequired: boolean;
  emailAdmissionMode: "ALL" | "ALLOWLIST";
  registrationIpPermits: number;
  registrationIpWindowSeconds: number;
  registrationIdentifierPermits: number;
  registrationIdentifierWindowSeconds: number;
  resendIpPermits: number;
  resendIpWindowSeconds: number;
  resendIdentifierPermits: number;
  resendIdentifierWindowSeconds: number;
  expectedVersion: number;
};

function durationSeconds(value: string) {
  const match = value.match(/^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:([\d.]+)S)?)?$/);
  if (!match) return 3600;
  return Number(match[1] ?? 0) * 86400 + Number(match[2] ?? 0) * 3600 + Number(match[3] ?? 0) * 60 + Number(match[4] ?? 0);
}

function policyForm(policy: RegistrationPolicy): PolicyForm {
  return {
    registrationEnabled: policy.registrationEnabled,
    invitationRequired: policy.invitationRequired,
    emailVerificationRequired: policy.emailVerificationRequired,
    emailAdmissionMode: policy.emailAdmissionMode,
    registrationIpPermits: policy.registrationIpLimit.permits,
    registrationIpWindowSeconds: durationSeconds(policy.registrationIpLimit.window),
    registrationIdentifierPermits: policy.registrationIdentifierLimit.permits,
    registrationIdentifierWindowSeconds: durationSeconds(policy.registrationIdentifierLimit.window),
    resendIpPermits: policy.resendIpLimit.permits,
    resendIpWindowSeconds: durationSeconds(policy.resendIpLimit.window),
    resendIdentifierPermits: policy.resendIdentifierLimit.permits,
    resendIdentifierWindowSeconds: durationSeconds(policy.resendIdentifierLimit.window),
    expectedVersion: policy.version,
  };
}

export function RegistrationGovernancePage() {
  const client = useQueryClient();
  const state = useQuery({
    queryKey: ["operator-registration-governance"],
    queryFn: () => api<RegistrationAdministration>("/api/v1/operator/super-admin/registration"),
  });
  const [policy, setPolicy] = useState<PolicyForm | null>(null);
  const [allowlist, setAllowlist] = useState("");
  const [maxUses, setMaxUses] = useState(1);
  const [expiresAt, setExpiresAt] = useState("");
  const [createdCode, setCreatedCode] = useState<string | null>(null);

  useEffect(() => {
    if (state.data?.policy && policy?.expectedVersion !== state.data.policy.version) {
      setPolicy(policyForm(state.data.policy));
    }
  }, [policy?.expectedVersion, state.data?.policy]);

  const refresh = () => client.invalidateQueries({ queryKey: ["operator-registration-governance"] });
  const savePolicy = useMutation({
    mutationFn: () => api<RegistrationPolicy>("/api/v1/operator/super-admin/registration/policy", {
      method: "PUT",
      body: JSON.stringify(policy),
    }),
    onSuccess: refresh,
  });
  const addAllowlist = useMutation({
    mutationFn: () => api("/api/v1/operator/super-admin/registration/allowlist", {
      method: "POST",
      body: JSON.stringify({ value: allowlist }),
    }),
    onSuccess: () => { setAllowlist(""); void refresh(); },
  });
  const removeAllowlist = useMutation({
    mutationFn: (id: string) => api<void>(`/api/v1/operator/super-admin/registration/allowlist/${id}`, { method: "DELETE" }),
    onSuccess: refresh,
  });
  const createInvitation = useMutation({
    mutationFn: () => api<{ code: string }>("/api/v1/operator/super-admin/registration/invitations", {
      method: "POST",
      body: JSON.stringify({ maxUses, expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null }),
    }),
    onSuccess: (created) => { setCreatedCode(created.code); void refresh(); },
  });
  const revokeInvitation = useMutation({
    mutationFn: ({ id, version }: { id: string; version: number }) => api<void>(`/api/v1/operator/super-admin/registration/invitations/${id}/revocation`, {
      method: "POST",
      body: JSON.stringify({ expectedVersion: version }),
    }),
    onSuccess: refresh,
  });

  return <>
    <PageHeader eyebrow="SUPER ADMIN" title="注册治理" description="控制注册开放范围、邮箱验证、邀请码和注册相关限流。更改保存后立即生效。" action={<StatusBadge tone={state.data?.policy.registrationEnabled ? "good" : "warning"}>{state.data?.policy.registrationEnabled ? "注册开放" : "注册关闭"}</StatusBadge>} />
    {state.isPending && <LoadingState label="正在读取注册策略" />}
    {state.error && <ErrorState error={state.error} retry={() => void state.refetch()} />}
    {policy && <Section title="注册与邮箱策略" description="关闭注册时邀请码也不能绕过；关闭强制验证只影响之后创建的账号。">
      <form className="settings-form registration-policy-form" onSubmit={(event) => { event.preventDefault(); savePolicy.mutate(); }}>
        <div className="field-grid three">
          <label className="check-row"><input type="checkbox" checked={policy.registrationEnabled} onChange={(event) => setPolicy({ ...policy, registrationEnabled: event.target.checked })} /><span>开放注册</span></label>
          <label className="check-row"><input type="checkbox" checked={policy.invitationRequired} onChange={(event) => setPolicy({ ...policy, invitationRequired: event.target.checked })} /><span>邀请码必填</span></label>
          <label className="check-row"><input type="checkbox" checked={policy.emailVerificationRequired} onChange={(event) => setPolicy({ ...policy, emailVerificationRequired: event.target.checked })} /><span>注册必须验证邮箱</span></label>
        </div>
        <label><span>允许注册的邮箱</span><select value={policy.emailAdmissionMode} onChange={(event) => setPolicy({ ...policy, emailAdmissionMode: event.target.value as PolicyForm["emailAdmissionMode"] })}><option value="ALL">所有有效邮箱</option><option value="ALLOWLIST">仅白名单邮箱和域名</option></select></label>
        <div className="field-grid">
          <LimitFields title="每 IP 注册" permits={policy.registrationIpPermits} seconds={policy.registrationIpWindowSeconds} onPermits={(value) => setPolicy({ ...policy, registrationIpPermits: value })} onSeconds={(value) => setPolicy({ ...policy, registrationIpWindowSeconds: value })} />
          <LimitFields title="每标识注册" permits={policy.registrationIdentifierPermits} seconds={policy.registrationIdentifierWindowSeconds} onPermits={(value) => setPolicy({ ...policy, registrationIdentifierPermits: value })} onSeconds={(value) => setPolicy({ ...policy, registrationIdentifierWindowSeconds: value })} />
          <LimitFields title="每 IP 重发" permits={policy.resendIpPermits} seconds={policy.resendIpWindowSeconds} onPermits={(value) => setPolicy({ ...policy, resendIpPermits: value })} onSeconds={(value) => setPolicy({ ...policy, resendIpWindowSeconds: value })} />
          <LimitFields title="每邮箱重发" permits={policy.resendIdentifierPermits} seconds={policy.resendIdentifierWindowSeconds} onPermits={(value) => setPolicy({ ...policy, resendIdentifierPermits: value })} onSeconds={(value) => setPolicy({ ...policy, resendIdentifierWindowSeconds: value })} />
        </div>
        <button className="primary-button" disabled={savePolicy.isPending} type="submit"><Save />保存策略</button>
        <MutationMessage error={savePolicy.error} success={savePolicy.isSuccess && "注册策略已生效"} />
      </form>
    </Section>}

    {state.data && <Section title="邮箱白名单" description="可添加完整邮箱，或以 @ 开头添加整个域名。仅在选择“仅白名单”时生效。">
      <form className="operator-search" onSubmit={(event) => { event.preventDefault(); addAllowlist.mutate(); }}><label><span>邮箱或 @domain</span><input required maxLength={254} value={allowlist} onChange={(event) => setAllowlist(event.target.value)} placeholder="user@example.com 或 @example.com" /></label><button className="secondary-button" disabled={addAllowlist.isPending} type="submit"><Plus />添加</button></form>
      <MutationMessage error={addAllowlist.error ?? removeAllowlist.error} success={addAllowlist.isSuccess && "白名单已更新"} />
      <div className="governance-list">{state.data.allowlist.map((entry) => <div key={entry.id}><MailCheck /><span><strong>{entry.type === "DOMAIN" ? `@${entry.value}` : entry.value}</strong><small>{entry.type === "DOMAIN" ? "域名" : "邮箱地址"}</small></span><button className="icon-button" aria-label="删除白名单" title="删除白名单" type="button" onClick={() => removeAllowlist.mutate(entry.id)}><Trash2 /></button></div>)}</div>
    </Section>}

    {state.data && <Section title="注册邀请码" description="邀请码原文只在创建时显示一次；可设置使用次数、有效期并随时撤销。">
      <form className="operator-action-form invitation-create-form" onSubmit={(event) => { event.preventDefault(); createInvitation.mutate(); }}><div className="field-grid"><label><span>最多使用次数</span><input required type="number" min={1} max={100000} value={maxUses} onChange={(event) => setMaxUses(Number(event.target.value))} /></label><label><span>失效时间（可选）</span><input type="datetime-local" value={expiresAt} onChange={(event) => setExpiresAt(event.target.value)} /></label></div><button className="primary-button" disabled={createInvitation.isPending} type="submit"><TicketCheck />创建邀请码</button></form>
      {createdCode && <div className="invitation-secret" role="status"><div><strong>新邀请码</strong><code>{createdCode}</code><span>关闭页面后无法再次查看。</span></div><button className="secondary-button" type="button" onClick={() => navigator.clipboard.writeText(createdCode)}><ClipboardCopy />复制</button></div>}
      <MutationMessage error={createInvitation.error ?? revokeInvitation.error} />
      <div className="governance-list invitation-list">{state.data.invitations.map((invitation) => <div key={invitation.id}><TicketCheck /><span><strong>尾号 {invitation.codeHint}</strong><small>{invitation.useCount} / {invitation.maxUses} 次 · {invitation.expiresAt ? `${formatDate(invitation.expiresAt)} 失效` : "永久有效"}</small></span><StatusBadge tone={invitation.status === "ACTIVE" ? "good" : "neutral"}>{invitation.status}</StatusBadge>{invitation.status === "ACTIVE" && <button className="icon-button" aria-label="撤销邀请码" title="撤销邀请码" type="button" onClick={() => revokeInvitation.mutate({ id: invitation.id, version: invitation.version })}><Ban /></button>}</div>)}</div>
    </Section>}

    {state.data && state.data.invitationUses.length > 0 && <Section title="邀请码使用记录"><div className="governance-list">{state.data.invitationUses.map((use) => <div key={`${use.invitationId}-${use.subjectId}`}><TicketCheck /><span><strong>{use.username ? `@${use.username}` : use.subjectId}</strong><small>邀请码 {use.invitationId.slice(0, 8)} · {formatDate(use.usedAt)}</small></span></div>)}</div></Section>}
  </>;
}

function LimitFields({ title, permits, seconds, onPermits, onSeconds }: { title: string; permits: number; seconds: number; onPermits: (value: number) => void; onSeconds: (value: number) => void }) {
  return <fieldset className="rate-limit-fields"><legend>{title}</legend><label><span>次数</span><input required type="number" min={1} max={100000} value={permits} onChange={(event) => onPermits(Number(event.target.value))} /></label><label><span>窗口（秒）</span><input required type="number" min={1} max={604800} value={seconds} onChange={(event) => onSeconds(Number(event.target.value))} /></label></fieldset>;
}
